/* eslint-disable react-hooks/exhaustive-deps */
import { API_STATUS } from 'core-lib/constants/network';
import { useFuelBalance } from 'core-lib/hooks/useFuelBalance';
import { useProfile } from 'core-lib/hooks/useProfile';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

import { customToast } from 'core-lib/utils/customToast';
import parkplusStoreV2RedirectionRoutes from '../../constants/parkplusStoreV2RedirectionRoutes';
import useCart from '../../hooks/useCart';
import useParkplusStoreV2 from '../../hooks/useParkplusStoreV2';
import notifyMe from '../../services/cart/notifyMe';
import { AddToCartType, CartItem } from '../../types/cart';
import Product from '../../types/product';
import ProductCardAOC from './ProductCardAOC';
import ProductDescriptionAOC from './ProductDescriptionAOC';

type AddToCartProps = {
  product: Product;
  type?: AddToCartType;
  isSmall?: boolean;
  section?: string;
  option?: boolean;
};

export interface AddToCartChildProps extends Product {
  isInStock: boolean;
  isInCart: boolean;
  isLoading: boolean;
  option?: boolean;
  size?: string;
  onAddToCart: (
    e: any,
    qty?: number,
    cartItemId?: number,
    isBuyNow?: boolean
  ) => void;
  onViewCart: (e) => void;
  onNotifyMe: (e) => void;
  balance: number;
  isSmall?: boolean;
  qty?: number;
  cartItemId?: number;
  section?: string;
}

export const AddToCart = ({
  product,
  type = AddToCartType.ProductCard,
  isSmall = false,
  section,
  option = false,
}: AddToCartProps) => {
  // state
  const [notifyMeApiStatus, setNotifyMeApiStatus] = useState(
    API_STATUS.default
  );

  // declarations
  const router = useRouter();

  // custom hooks
  const { balance } = useFuelBalance();
  const { cartData, cartApiStatus } = useParkplusStoreV2();
  const { handleUpdateCart } = useCart();
  const { phoneNumber } = useProfile();

  // constants
  const { lastSelectedProductSku } = cartData || {};

  const { isInCart, qty, cartItemId, maxCartQuantity } = useMemo(() => {
    let index = cartData?.data?.findIndex(
      (item: CartItem) => +item?.productId === +product?.productId
    );
    return {
      isInCart: index > -1,
      qty: index > -1 ? cartData?.data?.[index]?.quantity : 0,
      cartItemId: index > -1 && cartData?.data?.[index]?.cartItemId,
      maxCartQuantity: cartData?.data?.[index]?.maxCartQuantity,
    };
  }, [product, cartData]);

  const handleAddToCart = async (
    e,
    newQty = 1,
    cartItemId = null,
    isBuyNow = false
  ) => {
    e.stopPropagation();
    if (newQty > maxCartQuantity && newQty > qty) {
      customToast({
        label: `You can only add upto ${maxCartQuantity} of these items`,
        variant: 'criticalNotPriority',
      });
      return;
    }
    handleUpdateCart(
      {
        ...product,
        cartItemId: cartItemId,
        quantity: newQty,
      },
      isBuyNow
    );
  };

  const handleViewCart = (e) => {
    e.stopPropagation();
    router.push(parkplusStoreV2RedirectionRoutes.cart);
  };

  const handleNotifyMe = async (e) => {
    e.stopPropagation();
    setNotifyMeApiStatus(API_STATUS.default);
    const body = {
      notifyMeData: {
        product_id: product?.productId,
        sku: product?.productSku,
        phone_number: phoneNumber,
      },
    };
    const response = await notifyMe(body);
    if (response) {
      setNotifyMeApiStatus(API_STATUS.success);
    } else {
      setNotifyMeApiStatus(API_STATUS.failure);
    }
    toast.error("You'll be notified when the product is back in stock");
  };

  const isInStock = useMemo(() => product?.stock > 0, [product]);

  const isLoading = useMemo(
    () =>
      (cartApiStatus === API_STATUS.in_progress ||
        notifyMeApiStatus === API_STATUS.in_progress) &&
      product.productSku === lastSelectedProductSku,
    [cartApiStatus, notifyMeApiStatus]
  );

  const childProps = {
    ...product,
    isInStock,
    isInCart,
    isLoading,
    onAddToCart: handleAddToCart,
    onViewCart: handleViewCart,
    onNotifyMe: handleNotifyMe,
    cartApiStatus,
    cartData,
    balance,
    isSmall,
    qty,
    cartItemId,
    section,
    option,
  };

  switch (type) {
    case AddToCartType.ProductDescription:
      return <ProductDescriptionAOC {...childProps} />;
    default:
      return <ProductCardAOC {...childProps} />;
  }
};
